/**
 * Discord server icons — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_creator`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const discordIconsPack: ToilePack = {
    slug: 'discord-icons',
    category: 'creator',
    name: 'Discord server icons',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'gaming', label: 'Gaming', subject: 'a fox head in gaming headphones, ears up, eyes narrowed'},
        {key: 'art', label: 'Art club', subject: 'a cat head with a paintbrush behind one ear and a smudge of paint on its cheek'},
        {key: 'study', label: 'Study group', subject: 'a round owl head in large glasses with a pencil in its beak'},
    ],
    styles: [
        {key: 'bold', label: 'Bold flat', style: 'bold clean shapes with no outlines and gradient-free fills, the silhouette pared to read tiny', palette: 'Three flat colors, burnt orange, cream and charcoal.', framing: 'The head sits centred and large on a solid colored background, an even margin for the round crop.'},
        {key: 'gradient', label: 'Soft gradient', style: 'smooth rounded shapes over a soft two-tone gradient field with a subtle inner shadow', palette: 'A violet to indigo gradient behind a pale mascot.', framing: 'The head sits centred and large on the gradient background, the corners left empty.'},
        {key: 'sticker', label: 'Sticker', style: 'a chunky die-cut sticker look with a thick white border and a soft drop shadow', palette: 'Bright candy color inside the white border, five colors at most.', framing: 'The shape sits centred on a plain flat background with room to spare at every edge.'},
    ],
    directions: ['a robot head instead', 'for a book club', 'as a single bold letter'],
    seo: {
        title: 'Discord server icons: gaming, art, study mascots',
        metaDescription: 'Nine Discord icons — gaming, art community and study group mascots — in bold flat, soft gradient and sticker finishes, built to survive the round crop.',
        h1: 'Discord server icon designs: gaming, art and study mascots in three finishes',
        tagline: 'Nine icons, three mascots, three finishes — drawn to survive the round crop and read small.',
        intro: 'A Discord server icon has to survive a round crop and stay readable at forty-eight pixels. These nine are drawn that way: a fox for a gaming server, a cat for an art community and an owl for a study group, each in a bold flat, soft gradient or sticker finish. Save the icon, or ask for a different mascot in the same three treatments.',
        useCases: [
            "a new Discord server's icon",
            'a rebrand for an existing community',
            'a matching icon for a sub-server or bot',
            'a set to offer members as alternates',
        ],
        faq: [
            {q: 'What size does the icon export at?', a: 'Each design renders as a 1024×1024 square; Discord itself compresses and crops it to a circle on upload, and the mascot is drawn centred with margin so that crop never clips it.'},
            {q: 'Can I get a different mascot, like a robot or a single letter?', a: "Yes — type the mascot into the direction field and it's redrawn in the same three finishes."},
            {q: 'Is there any text or lettering on the icon?', a: 'No — these are mascot-only icons with no server name lettered in, so the same icon works across a rename.'},
        ],
    },
}
